import React, { useEffect, useState } from "react";
import { api, fmtINR, fmtDate, downloadBlob, formatErr, today } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, Card, Empty } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Link, useNavigate } from "react-router-dom";

import {
  Plus,
  FileDown,
  Eye,
  FileSpreadsheet,
  Trash2,
  Share2,
  Pencil,
  Search,
  IndianRupee,
  User,
  Store,
  Phone,
  Send
} from "lucide-react";

import { toast } from "sonner";

const MODES = ["Cash", "UPI", "Bank Transfer", "Cheque"];

function buildOrderShareText(o, cust) {
  const lines = [
    `*Omkar Milk Distributor*`,
    `Invoice: *${o.invoice_no}*`,
    `Date: ${fmtDate(o.order_date)}`,
    `Bill To: ${o.customer_name}`,
    "",
    "Items:",
    ...o.items.map((it, i) => `${i+1}. ${it.product_name} — ${Number(it.quantity)} ${it.unit} × ₹${Number(it.rate).toFixed(2)} = ₹${Number(it.amount).toFixed(2)}`),
    "",
    `*Invoice Total: ₹${Number(o.total_amount).toFixed(2)}*`,
  ];
  if (cust) {
    if (cust.advance > 0) {
      lines.push(`*Remaining Balance (Advance): ₹${Number(cust.advance).toFixed(2)}*`);
    } else {
      lines.push(`*Remaining Balance (Outstanding): ₹${Number(cust.outstanding || 0).toFixed(2)}*`);
    }
  }
  lines.push("");
  lines.push("Thank you for your business!");
  return lines.join("\n");
}

export default function Orders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [frm, setFrm] = useState("");
  const [to, setTo] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Payment modal state for Quick Order Payment
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [targetOrder, setTargetOrder] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    customer_id: "",
    amount: "",
    payment_mode: "Cash",
    reference_no: "",
    payment_date: today(),
    remarks: "",
  });

  // WhatsApp Share modal state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareOrder, setShareOrder] = useState(null);
  const [shareCustomer, setShareCustomer] = useState(null);

  function load() {
    const params = {};
    if (frm) params.frm = frm;
    if (to) params.to = to;
    api.get("/orders", { params }).then(r => setOrders(r.data));
    api.get("/customers").then(r => setCustomers(r.data));
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function remove(o) {
    if (!window.confirm(`Delete ${o.invoice_no}?`)) return;
    await api.delete(`/orders/${o.id}`);
    toast.success("Order deleted"); load();
  }

  function handleShareClick(o) {
    const cust = customers.find(c => c.id === o.customer_id);
    if (cust && cust.mobile_secondary) {
      setShareOrder(o);
      setShareCustomer(cust);
      setShareModalOpen(true);
    } else {
      executeShare(o, cust, cust?.mobile || "");
    }
  }

  function executeShare(o, cust, phoneInput) {
    const phoneRaw = (phoneInput || "").replace(/\D/g, "");
    const phone = phoneRaw.length === 10 ? `91${phoneRaw}` : phoneRaw;
    const text = encodeURIComponent(buildOrderShareText(o, cust));
    const url = phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(url, "_blank", "noopener,noreferrer");
    setShareModalOpen(false);
  }

  function openPaymentForOrder(o) {
    setTargetOrder(o);
    setPaymentForm({
      customer_id: o.customer_id,
      amount: String(o.total_amount || ""),
      payment_mode: "Cash",
      reference_no: o.invoice_no || "",
      payment_date: today(),
      remarks: `Payment for Invoice ${o.invoice_no}`,
    });
    setPaymentModalOpen(true);
  }

  async function saveOrderPayment() {
    if (!paymentForm.customer_id) return toast.error("Select customer");
    if (!Number(paymentForm.amount)) return toast.error("Enter valid amount");
    try {
      await api.post("/payments", {
        ...paymentForm,
        amount: Number(paymentForm.amount),
      });
      toast.success(`Payment of ₹${paymentForm.amount} recorded for ${targetOrder?.invoice_no || 'order'}`);
      setPaymentModalOpen(false);
      setTargetOrder(null);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Failed to record payment");
    }
  }

  const filteredOrders = orders.filter(o => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase().trim();
    const invMatch = (o.invoice_no || "").toLowerCase().includes(query);
    const custMatch = (o.customer_name || "").toLowerCase().includes(query);
    const itemMatch = (o.items || []).some(it => (it.product_name || "").toLowerCase().includes(query));
    const statusMatch = (o.status || "").toLowerCase().includes(query);
    return invMatch || custMatch || itemMatch || statusMatch;
  });

  return (
    <div data-testid="orders-page">
      <PageHeader
        eyebrow="Sales"
        title={user?.role === "customer" ? "My Orders" : "Orders & Invoices"}
        description="Track daily orders, auto-priced from each customer's rate sheet."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => downloadBlob("/export/orders.xlsx", "orders.xlsx")} data-testid="orders-export-xlsx"><FileSpreadsheet size={14} className="mr-1"/>Excel</Button>
            {user?.role === "admin" && (
              <Button onClick={() => navigate("/orders/new")} className="bg-cobalt-500 hover:bg-cobalt-700 text-white" data-testid="orders-create-btn"><Plus size={16} className="mr-1"/>New Order</Button>
            )}
          </div>
        }
      />

      <Card padding="p-4" className="mb-4">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">From</label>
              <Input type="date" value={frm} onChange={e=>setFrm(e.target.value)} className="mt-1"/>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">To</label>
              <Input type="date" value={to} onChange={e=>setTo(e.target.value)} className="mt-1"/>
            </div>
            <Button variant="outline" onClick={load}>Filter</Button>
          </div>

          <div className="w-full sm:w-72">
            <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Search Orders</label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <Input
                type="text"
                placeholder="Search invoice, customer, product..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-9 bg-white"
                data-testid="input-search-orders"
              />
            </div>
          </div>
        </div>
      </Card>

      <Card padding="p-0">
        {filteredOrders.length === 0 ? <div className="p-8"><Empty title={searchQuery ? "No matching orders found" : "No orders yet"}/></div> : (
          <div className="overflow-x-auto">
            <table className="ddms-table" data-testid="orders-table">
              <thead>
                <tr><th>Date</th><th>Invoice</th><th>Customer</th><th>Items</th><th className="num">Total</th><th>Status</th><th className="num">Actions</th></tr>
              </thead>
              <tbody>
                {filteredOrders.map(o => (
                  <tr key={o.id}>
                    <td className="font-mono text-xs">{fmtDate(o.order_date)}</td>
                    <td className="font-mono text-xs font-semibold text-cobalt-600">{o.invoice_no}</td>
                    <td className="font-medium">{o.customer_name}</td>
                    <td>{o.items.length}</td>
                    <td className="num font-semibold">{fmtINR(o.total_amount)}</td>
                    <td><span className="px-2 py-0.5 rounded-full text-[11px] bg-emerald-50 text-emerald-700 font-medium">{o.status}</span></td>
                    <td className="num">
                      {user?.role === "admin" && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openPaymentForOrder(o)}
                            className="mr-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50 bg-emerald-50/40"
                            title="Add Payment"
                            data-testid={`add-payment-btn-${o.id}`}
                          >
                            <IndianRupee size={12} className="mr-0.5" /> Pay
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/orders/new?id=${o.id}`)}
                            className="mr-2"
                            title="Edit"
                          >
                            <Pencil size={12}/>
                          </Button>
                        </>
                      )}
                      <Link to={`/orders/${o.id}`}><Button size="sm" variant="outline" className="mr-2"><Eye size={12}/></Button></Link>
                      <Button size="sm" variant="outline" onClick={() => handleShareClick(o)} className="mr-2 border-emerald-200 text-emerald-700 hover:bg-emerald-50" title="Share on WhatsApp" data-testid={`whatsapp-share-${o.id}`}><Share2 size={12}/></Button>
                      <Button size="sm" variant="outline" onClick={() => downloadBlob(`/export/invoice/${o.id}.pdf`, `${o.invoice_no}.pdf`)} className="mr-2"><FileDown size={12}/></Button>
                      {user?.role === "admin" && (
                        <Button size="sm" variant="outline" onClick={()=>remove(o)} className="text-rose-600 border-rose-200 hover:bg-rose-50"><Trash2 size={12}/></Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Quick Payment Modal */}
      <Dialog open={paymentModalOpen} onOpenChange={setPaymentModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Payment for {targetOrder?.invoice_no}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2">
              <Label>Customer Name</Label>
              <Input value={targetOrder?.customer_name || ""} disabled className="bg-slate-100 font-medium" />
            </div>
            <div>
              <Label>Payment Amount (₹)</Label>
              <Input
                type="number"
                value={paymentForm.amount}
                onChange={e => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                placeholder="0.00"
                data-testid="input-quick-payment-amount"
              />
            </div>
            <div>
              <Label>Payment Mode</Label>
              <Select value={paymentForm.payment_mode} onValueChange={v => setPaymentForm({ ...paymentForm, payment_mode: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MODES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Payment Date</Label>
              <Input
                type="date"
                value={paymentForm.payment_date}
                onChange={e => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Reference No</Label>
              <Input
                value={paymentForm.reference_no}
                onChange={e => setPaymentForm({ ...paymentForm, reference_no: e.target.value })}
                placeholder="UPI / Cheque / Ref"
              />
            </div>
            <div className="col-span-2">
              <Label>Remarks</Label>
              <Textarea
                rows={2}
                value={paymentForm.remarks}
                onChange={e => setPaymentForm({ ...paymentForm, remarks: e.target.value })}
                placeholder="Optional notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentModalOpen(false)}>Cancel</Button>
            <Button onClick={saveOrderPayment} className="bg-emerald-600 hover:bg-emerald-700 text-white">Record Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WhatsApp Recipient Selection Modal */}
      <Dialog open={shareModalOpen} onOpenChange={setShareModalOpen}>
        <DialogContent className="sm:max-w-md p-0 overflow-hidden border-0 shadow-2xl rounded-2xl">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-700 p-5 text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-white text-lg font-heading font-semibold">
                <div className="p-2 bg-white/10 backdrop-blur-md rounded-xl">
                  <Share2 size={20} className="text-white" />
                </div>
                Share Invoice on WhatsApp
              </DialogTitle>
            </DialogHeader>
            <div className="mt-3 flex items-center justify-between text-xs text-emerald-100 bg-white/10 backdrop-blur-md rounded-lg px-3 py-2 border border-white/15">
              <div>
                <span className="opacity-75">Invoice:</span> <strong className="text-white font-mono text-sm">{shareOrder?.invoice_no}</strong>
              </div>
              <div className="text-right">
                <span className="opacity-75">Total:</span> <strong className="text-white font-mono text-sm">₹{Number(shareOrder?.total_amount || 0).toFixed(2)}</strong>
              </div>
            </div>
          </div>

          <div className="p-5 bg-slate-50/50">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5">
              <Store size={14} className="text-cobalt-600" />
              <span>{shareCustomer?.business_name}</span>
              <span className="text-slate-400 font-normal">({shareCustomer?.customer_name})</span>
            </div>

            <div className="space-y-3">
              <div
                onClick={() => executeShare(shareOrder, shareCustomer, shareCustomer?.mobile)}
                className="group relative flex items-center justify-between p-4 bg-white hover:bg-emerald-50/60 border-2 border-emerald-500/30 hover:border-emerald-500 rounded-xl cursor-pointer transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <div className="flex items-center gap-3.5">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold transition-transform group-hover:scale-105">
                    <User size={18} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900 text-sm">Owner (Primary)</span>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-100 text-emerald-800">Primary</span>
                    </div>
                    <div className="text-xs font-mono text-slate-600 mt-0.5 flex items-center gap-1">
                      <Phone size={12} className="text-slate-400" />
                      {shareCustomer?.mobile || "N/A"}
                    </div>
                  </div>
                </div>
                <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 transform group-hover:translate-x-0 -translate-x-2">
                  <Send size={14} />
                </div>
              </div>

              {shareCustomer?.mobile_secondary && (
                <div
                  onClick={() => executeShare(shareOrder, shareCustomer, shareCustomer?.mobile_secondary)}
                  className="group relative flex items-center justify-between p-4 bg-white hover:bg-cobalt-50/60 border-2 border-slate-200 hover:border-cobalt-400 rounded-xl cursor-pointer transition-all duration-200 shadow-sm hover:shadow-md"
                >
                  <div className="flex items-center gap-3.5">
                    <div className="w-10 h-10 rounded-full bg-cobalt-100 text-cobalt-700 flex items-center justify-center font-bold transition-transform group-hover:scale-105">
                      <Store size={18} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-900 text-sm">Cashier / Shop Manager</span>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-700">Secondary</span>
                      </div>
                      <div className="text-xs font-mono text-slate-600 mt-0.5 flex items-center gap-1">
                        <Phone size={12} className="text-slate-400" />
                        {shareCustomer?.mobile_secondary}
                      </div>
                    </div>
                  </div>
                  <div className="w-8 h-8 rounded-full bg-cobalt-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 transform group-hover:translate-x-0 -translate-x-2">
                    <Send size={14} />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="px-5 py-3.5 bg-slate-100 border-t border-slate-200 flex justify-end">
            <Button variant="outline" size="sm" onClick={() => setShareModalOpen(false)} className="rounded-lg text-xs font-medium">
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
